import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface Holiday {
  id: string;
  date: string;
  name: string;
  isFullDay: boolean;
  startTime: string | null;
  endTime: string | null;
  createdAt: string;
}

export function useHolidays() {
  return useQuery<Holiday[]>({
    queryKey: ['holidays'],
    queryFn: async () => {
      const response = await fetch(apiUrl('/api/v1/admin/holidays'), {
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail = (errorBody as Record<string, Record<string, string>>)?.error?.message || response.statusText;
        throw new Error(detail);
      }

      const json = await response.json();
      return (json as Record<string, unknown>).data as Holiday[];
    },
  });
}

export function useCreateHoliday() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { date: string; name: string; isFullDay?: boolean }) => {
      const response = await fetch(apiUrl('/api/v1/admin/holidays'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ ...data, isFullDay: data.isFullDay ?? true }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail = (errorBody as Record<string, Record<string, string>>)?.error?.message || response.statusText;
        throw new Error(detail);
      }

      const json = await response.json();
      return (json as Record<string, unknown>).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-availability'] });
    },
  });
}

export function useDeleteHoliday() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(apiUrl(`/api/v1/admin/holidays/${id}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail = (errorBody as Record<string, Record<string, string>>)?.error?.message || response.statusText;
        throw new Error(detail);
      }

      const json = await response.json();
      return (json as Record<string, unknown>).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-availability'] });
    },
  });
}
